import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY_GH = { name:'', unit_id:'', company_id:'', city:'', address:'', status:'active' };
const EMPTY_ROOM = { room_name:'', room_type:'single', capacity:1, floor:'', amenities:'', is_active:true };
const ROOM_TYPES = ['single','double','suite','dormitory','vip'];
const STATUS_COLORS = {
  active:'bg-green-100 text-green-700',
  inactive:'bg-gray-100 text-gray-500'
};

export default function GuestHouses() {
  const [houses, setHouses] = useState([]);
  const [units, setUnits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showGHForm, setShowGHForm] = useState(false);
  const [ghForm, setGhForm] = useState(EMPTY_GH);
  const [editingGH, setEditingGH] = useState(null);

  const [selectedGH, setSelectedGH] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomLoading, setRoomLoading] = useState(false);

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/guest-houses'); setHouses(r.data); }
    catch (err) { console.error('guest-houses:', err); setHouses([]); }
    try { const r = await api.get('/api/units'); setUnits(r.data); }
    catch (err) { console.error('units:', err); setUnits([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch (err) { console.error('companies:', err); setCompanies([]); }
    }
    setLoading(false);
  };

  const loadRooms = async (ghId) => {
    setRoomLoading(true);
    try {
      const res = await api.get(`/api/guest-houses/${ghId}/rooms`);
      setRooms(res.data);
    } catch {}
    finally { setRoomLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  // Guest House CRUD
  const handleGHSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGH) await api.put(`/api/guest-houses/${editingGH}`, ghForm);
      else await api.post('/api/guest-houses', ghForm);
      setShowGHForm(false); setGhForm(EMPTY_GH); setEditingGH(null);
      load();
      if (selectedGH === editingGH) setSelectedGH(null);
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleGHDelete = async (id, name) => {
    if (!confirmDelete('guest house', name)) return;
    try {
      await api.delete(`/api/guest-houses/${id}`);
      load();
      if (selectedGH === id) { setSelectedGH(null); setRooms([]); }
    } catch (err) { handleDeleteError(err); }
  };

  const editGH = (gh) => {
    setGhForm({
      name: gh.name, unit_id: gh.unit_id || '', company_id: gh.company_id || '',
      city: gh.city || '', address: gh.address || '', status: gh.status
    });
    setEditingGH(gh.id); setShowGHForm(true);
  };

  // Room CRUD
  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoom) {
        await api.put(`/api/guest-houses/rooms/${editingRoom}`, roomForm);
      } else {
        await api.post(`/api/guest-houses/${selectedGH}/rooms`, roomForm);
      }
      setShowRoomForm(false); setRoomForm(EMPTY_ROOM); setEditingRoom(null);
      loadRooms(selectedGH);
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleRoomDelete = async (roomId) => {
    if (!confirmDelete('room')) return;
    try {
      await api.delete(`/api/guest-houses/rooms/${roomId}`);
      loadRooms(selectedGH);
    } catch (err) { handleDeleteError(err); }
  };

  const handleRoomToggle = async (room) => {
    try {
      await api.put(`/api/guest-houses/rooms/${room.id}`, { ...room, is_active: !room.is_active });
      loadRooms(selectedGH);
    } catch (err) { alert('Update failed'); }
  };

  const editRoom = (room) => {
    setRoomForm({
      room_name: room.room_name, room_type: room.room_type,
      capacity: room.capacity, floor: room.floor || '',
      amenities: room.amenities || '', is_active: room.is_active
    });
    setEditingRoom(room.id); setShowRoomForm(true);
  };

  const selectGH = (id) => {
    setSelectedGH(id);
    setRooms([]);
    loadRooms(id);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Guest Houses</h1>
          <p className="text-gray-500 text-sm">{houses.length} guest house(s)</p>
        </div>
        {isManager && (
          <button onClick={() => { setGhForm(EMPTY_GH); setEditingGH(null); setShowGHForm(true); }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ Add Guest House
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Guest House list */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-gray-400 p-8 text-center">Loading...</div>
          ) : houses.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-gray-400">
              <p className="text-4xl mb-2">🏠</p>
              <p>No guest houses yet</p>
            </div>
          ) : (
            houses.map(gh => (
              <div key={gh.id}
                onClick={() => selectGH(gh.id)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                  selectedGH === gh.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-100 hover:shadow-md'
                }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{gh.name}</p>
                    {gh.city && <p className="text-gray-500 text-xs">📍 {gh.city}</p>}
                    {gh.unit_name && <p className="text-gray-500 text-xs">🏭 {gh.unit_name}</p>}
                    {gh.company_name && <p className="text-gray-500 text-xs">🏢 {gh.company_name}</p>}
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                        {gh.total_rooms || 0} rooms
                      </span>
                      {gh.occupied_rooms > 0 && (
                        <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">
                          {gh.occupied_rooms} occupied
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[gh.status]}`}>
                      {gh.status}
                    </span>
                    {isManager && (
                      <div className="flex gap-1">
                        <button onClick={e => { e.stopPropagation(); editGH(gh); }}
                          className="text-xs px-2 py-1 border rounded hover:bg-gray-50">✏️</button>
                        {isAdmin && (
                          <button onClick={e => { e.stopPropagation(); handleGHDelete(gh.id, gh.name); }}
                            className="text-xs px-2 py-1 border rounded hover:bg-red-50 hover:text-red-600">🗑️</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right: Rooms panel */}
        <div className="lg:col-span-2">
          {!selectedGH ? (
            <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
              <p className="text-4xl mb-2">🛏️</p>
              <p>Select a guest house to manage rooms</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-gray-800">
                  Rooms — {houses.find(h => h.id === selectedGH)?.name}
                </h2>
                {isManager && (
                  <button onClick={() => { setRoomForm(EMPTY_ROOM); setEditingRoom(null); setShowRoomForm(true); }}
                    className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                    ➕ Add Room
                  </button>
                )}
              </div>
              {roomLoading ? (
                <div className="p-8 text-center text-gray-400">Loading rooms...</div>
              ) : rooms.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No rooms added yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Room</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Type</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Cap</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Floor</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Status</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rooms.map(r => (
                        <tr key={r.id} className={`border-t ${!r.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                          <td className="px-4 py-2 font-medium">{r.room_name}</td>
                          <td className="px-4 py-2 capitalize">{r.room_type}</td>
                          <td className="px-4 py-2">{r.capacity}</td>
                          <td className="px-4 py-2">{r.floor || '—'}</td>
                          <td className="px-4 py-2">
                            {r.booking_status === 'occupied' ? (
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs">
                                Occupied — {r.current_guest}
                              </span>
                            ) : r.is_active ? (
                              <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">Available</span>
                            ) : (
                              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">Disabled</span>
                            )}
                          </td>
                          {isManager && (
                            <td className="px-4 py-2">
                              <div className="flex gap-1">
                                <button onClick={() => editRoom(r)}
                                  className="text-xs px-2 py-1 border rounded hover:bg-gray-50">✏️</button>
                                <button onClick={() => handleRoomToggle(r)}
                                  className={`text-xs px-2 py-1 border rounded ${r.is_active ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'}`}>
                                  {r.is_active ? '🚫' : '✅'}
                                </button>
                                {isAdmin && r.booking_status !== 'occupied' && (
                                  <button onClick={() => handleRoomDelete(r.id)}
                                    className="text-xs px-2 py-1 border rounded hover:bg-red-50 hover:text-red-600">🗑️</button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Guest House Form Modal */}
      {showGHForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{editingGH ? 'Edit Guest House' : 'Add Guest House'}</h2>
              <button onClick={() => { setShowGHForm(false); setEditingGH(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleGHSubmit} className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest House Name *</label>
                <input value={ghForm.name} onChange={e => setGhForm({...ghForm,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select value={ghForm.unit_id} onChange={e => setGhForm({...ghForm,unit_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Select Unit —</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                {isAdmin ? (
                  <select value={ghForm.company_id} onChange={e => setGhForm({...ghForm,company_id:e.target.value})} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select Company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input type="text" disabled value={user?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input value={ghForm.city} onChange={e => setGhForm({...ghForm,city:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={ghForm.status} onChange={e => setGhForm({...ghForm,status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea value={ghForm.address} onChange={e => setGhForm({...ghForm,address:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editingGH ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowGHForm(false); setEditingGH(null); }}
                  className="border px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Room Form Modal */}
      {showRoomForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{editingRoom ? 'Edit Room' : 'Add Room'}</h2>
              <button onClick={() => { setShowRoomForm(false); setEditingRoom(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleRoomSubmit} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Number / Name *</label>
                <input value={roomForm.room_name} onChange={e => setRoomForm({...roomForm,room_name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                <select value={roomForm.room_type} onChange={e => setRoomForm({...roomForm,room_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {ROOM_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                <input type="number" min={1} value={roomForm.capacity}
                  onChange={e => setRoomForm({...roomForm,capacity:parseInt(e.target.value)||1})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
                <input value={roomForm.floor} onChange={e => setRoomForm({...roomForm,floor:e.target.value})}
                  placeholder="e.g. Ground, 1st"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <input value={roomForm.amenities} onChange={e => setRoomForm({...roomForm,amenities:e.target.value})}
                  placeholder="AC, TV, Attached Bath..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {editingRoom && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={roomForm.is_active ? 'active' : 'disabled'}
                    onChange={e => setRoomForm({...roomForm,is_active:e.target.value==='active'})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              )}
              <div className={editingRoom ? '' : 'col-span-2'} style={editingRoom ? {} : {}}>
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                    {editingRoom ? 'Update' : 'Add Room'}
                  </button>
                  <button type="button" onClick={() => { setShowRoomForm(false); setEditingRoom(null); }}
                    className="border px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
